import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Shield, Gift, ArrowRight, AlertCircle, Star, Award, Clock, Users } from 'lucide-react';
import Navbar from '../Common/Navbar';
import SecondaryFooter from '../Common/SecondaryFooter';
import './FreeActivation.css';

const FreeActivation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [planDetails, setPlanDetails] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  const [activationSuccess, setActivationSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [loading, setLoading] = useState(true);

  // Load plan details from location.state OR localStorage
  useEffect(() => {
    const loadPlanDetails = () => {
      let data = location.state;

      if (!data) {
        const storedData = localStorage.getItem('freeActivationData');
        if (storedData) {
          try {
            data = JSON.parse(storedData);
          } catch (err) {
            console.error('Error parsing stored activation data:', err);
          }
        }
      } else {
        localStorage.setItem('freeActivationData', JSON.stringify(data));
      }

      setPlanDetails(data);
      setLoading(false);
    };

    loadPlanDetails();
  }, [location.state]);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      if (planDetails) {
        localStorage.setItem('freeActivationData', JSON.stringify(planDetails));
      }
      navigate('/login', { 
        state: { 
          from: '/payment/free-activation',
          message: 'Please log in to activate your free plan' 
        } 
      });
    }
  }, [planDetails, navigate]);

  // Countdown after successful activation
  useEffect(() => {
    if (activationSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (activationSuccess && countdown === 0) {
      localStorage.removeItem('freeActivationData');
      navigate('/dashboard');
    }
  }, [activationSuccess, countdown, navigate]);

  const handleActivation = async () => {
    if (!planDetails) {
      setError('Plan details are missing. Please try again.');
      return;
    }

    setIsActivating(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');

      const response = await fetch('http://localhost:8082/api/subscriptions/activate-free', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: userData.userId || userData.id,
          productId: planDetails.productId,
          planName: planDetails.selectedPlan.planName,
          planType: planDetails.selectedPlan.duration,
          planAmount: 0,
          status: 'ACTIVE'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to activate free plan');
      }

      const result = await response.json();
      console.log('Free plan activated:', result);

      localStorage.removeItem('freeActivationData');
      setActivationSuccess(true);

    } catch (err) {
      console.error('Activation error:', err);
      setError('Failed to activate free plan. Please try again or contact support.');
    } finally {
      setIsActivating(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="free-activation-page">
          <div className="loading-container">
            <div className="loading-spinner-wrapper">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="loading-text">Loading activation details...</p>
            </div>
          </div>
        </div>
        <SecondaryFooter />
      </>
    );
  }

  // No plan details
  if (!planDetails) {
    return (
      <>
        <Navbar />
        <div className="free-activation-page">
          <div className="error-container">
            <div className="error-card">
              <div className="error-icon-wrapper">
                <AlertCircle size={64} />
              </div>
              <h2 className="error-title">No Plan Selected</h2>
              <p className="error-message">
                We couldn't find any plan details. Please select a plan from the products page.
              </p>
              <button className="btn-primary-large" onClick={() => navigate('/')}>
                Browse Products
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
        <SecondaryFooter />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="free-activation-page">
        {/* Success State */}
        {activationSuccess ? (
          <div className="success-container">
            <div className="success-content">
              <div className="success-animation">
                <div className="success-checkmark">
                  <div className="checkmark-circle">
                    <Check size={60} strokeWidth={3} />
                  </div>
                </div>
                <div className="success-sparkles">
                  <Sparkles className="sparkle sparkle-1" size={24} />
                  <Sparkles className="sparkle sparkle-2" size={20} />
                  <Sparkles className="sparkle sparkle-3" size={28} />
                  <Sparkles className="sparkle sparkle-4" size={22} />
                </div>
              </div>

              <h1 className="success-title">🎉 Congratulations!</h1>
              <p className="success-subtitle">Your free plan has been successfully activated</p>

              <div className="success-features">
                <div className="success-feature">
                  <div className="feature-icon-circle">
                    <Gift size={24} />
                  </div>
                  <span>{planDetails.selectedPlan.planName}</span>
                </div>
                <div className="success-feature">
                  <div className="feature-icon-circle">
                    <Zap size={24} />
                  </div>
                  <span>Instant Access</span>
                </div>
                <div className="success-feature">
                  <div className="feature-icon-circle">
                    <Shield size={24} />
                  </div>
                  <span>No Credit Card Required</span>
                </div>
              </div>

              <div className="redirect-section">
                <div className="countdown-badge">
                  <Clock size={18} />
                  <span>Redirecting in {countdown}s</span>
                </div>
                <button
                  className="btn-dashboard"
                  onClick={() => {
                    localStorage.removeItem('freeActivationData');
                    navigate('/dashboard');
                  }}
                >
                  Go to Dashboard Now
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Activation Form */
          <div className="activation-container">
            <div className="activation-content">
              
              {/* Hero Section */}
              <div className="activation-hero">
                <div className="hero-badge">
                  <Sparkles size={16} />
                  <span>Limited Time Offer</span>
                </div>
                <h1 className="hero-title">
                  Activate Your <span className="gradient-text">Free Plan</span>
                </h1>
                <p className="hero-subtitle">
                  Get instant access to {planDetails.productName} with no credit card required
                </p>
              </div>

              {/* Main Grid */}
              <div className="activation-grid">
                
                {/* Left Side - Benefits & Features */}
                <div className="benefits-column">
                  
                  {/* Product Card */}
                  <div className="product-showcase-card">
                    <div className="product-header">
                      <div className="product-icon">
                        <Gift size={28} />
                      </div>
                      <div className="product-info">
                        <h3>{planDetails.productName}</h3>
                        <div className="plan-badge-inline">
                          <Star size={14} />
                          <span>{planDetails.selectedPlan.planName}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Benefits */}
                  <div className="benefits-card">
                    <h3 className="card-title">
                      <Award size={20} />
                      What You Get
                    </h3>
                    <div className="benefits-list">
                      <div className="benefit-item-modern">
                        <div className="benefit-icon-wrapper">
                          <Check size={18} />
                        </div>
                        <div className="benefit-content">
                          <h4>Instant Access</h4>
                          <p>Start using all features immediately after activation</p>
                        </div>
                      </div>

                      <div className="benefit-item-modern">
                        <div className="benefit-icon-wrapper">
                          <Check size={18} />
                        </div>
                        <div className="benefit-content">
                          <h4>No Credit Card</h4>
                          <p>Completely free with no payment information required</p>
                        </div>
                      </div>

                      <div className="benefit-item-modern">
                        <div className="benefit-icon-wrapper">
                          <Check size={18} />
                        </div>
                        <div className="benefit-content">
                          <h4>Full Support</h4>
                          <p>Access to our dedicated support team whenever you need help</p>
                        </div>
                      </div>

                      <div className="benefit-item-modern">
                        <div className="benefit-icon-wrapper">
                          <Check size={18} />
                        </div>
                        <div className="benefit-content">
                          <h4>Cancel Anytime</h4>
                          <p>No commitments, no contracts, no hidden fees</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="trust-badges">
                    <div className="trust-badge">
                      <Shield size={18} />
                      <span>Secure & Private</span>
                    </div>
                    <div className="trust-badge">
                      <Zap size={18} />
                      <span>Instant Setup</span>
                    </div>
                    <div className="trust-badge">
                      <Users size={18} />
                      <span>10K+ Active Users</span>
                    </div>
                  </div>

                </div>

                {/* Right Side - Order Summary */}
                <div className="summary-column">
                  
                  <div className="summary-card">
                    <div className="summary-header">
                      <h2>Order Summary</h2>
                      <div className="free-badge-large">
                        <Gift size={18} />
                        <span>FREE</span>
                      </div>
                    </div>

                    <div className="summary-details">
                      <div className="detail-item">
                        <div className="detail-label">
                          <Sparkles size={16} />
                          <span>Product</span>
                        </div>
                        <div className="detail-value">{planDetails.productName}</div>
                      </div>

                      <div className="detail-item">
                        <div className="detail-label">
                          <Gift size={16} />
                          <span>Plan</span>
                        </div>
                        <div className="detail-value">{planDetails.selectedPlan.planName}</div>
                      </div>

                      <div className="detail-item">
                        <div className="detail-label">
                          <Clock size={16} />
                          <span>Billing Cycle</span>
                        </div>
                        <div className="detail-value">
                          {planDetails.selectedPlan.duration === 'M' ? 'Monthly' : 'Yearly'}
                        </div>
                      </div>
                    </div>

                    <div className="summary-divider"></div>

                    <div className="pricing-breakdown">
                      <div className="pricing-item">
                        <span>Subscription Fee</span>
                        <span className="price-free-text">FREE</span>
                      </div>
                      <div className="pricing-item">
                        <span>GST (18%)</span>
                        <span>₹0.00</span>
                      </div>
                      <div className="pricing-item">
                        <span>Setup Fee</span>
                        <span>₹0.00</span>
                      </div>
                    </div>

                    <div className="summary-divider"></div>

                    <div className="total-section">
                      <span className="total-label">Total Amount</span>
                      <span className="total-amount">
                        <span className="currency">₹</span>
                        <span className="amount">0</span>
                        <span className="free-tag">FREE</span>
                      </span>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="error-alert">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                      </div>
                    )}

                    {/* Activation Button */}
                    <button
                      className="btn-activate"
                      onClick={handleActivation}
                      disabled={isActivating}
                    >
                      {isActivating ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          <span>Activating Your Plan...</span>
                        </>
                      ) : (
                        <>
                          <Zap size={20} />
                          <span>Activate Free Plan Now</span>
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>

                    <p className="terms-text">
                      By activating, you agree to our{' '}
                      <a href="/terms" target="_blank">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank">Privacy Policy</a>
                    </p>

                    {/* Security Note */}
                    <div className="security-note">
                      <Shield size={16} />
                      <span>Your data is secure and protected</span>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>
        )}
      </div>

      <SecondaryFooter />
    </>
  );
};

export default FreeActivation;